import { memo, useMemo } from "react";
import { TemplatePage } from "../../2_ksbHub/0_TemplatePage/TemplatePage";
import { useAppContext } from "../../2_ksbHub/context/appContext";
import { SondertagContext } from "./context/sondertagContext";
import LoadingAppScreen from "./components/LoadingAppScreen";
import EmptyAdminState from "./components/EmptyAdminState";
import EmptyUserState from "./components/EmptyUserState";
import AddSonderEventDialog from "./components/AddSonderEventDialog";

import sondertagService from "./services/sondertagService";

import useSondertagState from "./hooks/useSondertagState";
import useSondertagSocket from "./hooks/useSondertagSocket";
import useSonderLoading from "./hooks/useSonderLoading";
import useSonderDialogs from "./hooks/useSonderDialogs";

import { isSondertageEventAdmin } from "./utils/sonderPermissions";
import SondertageBody from "./components/SondertageBody";
import { computeTeacherStats } from "./teacherStats/computeTeacherStats";

function SondertageApp() {
  const { currentUser, isAnAdmin } = useAppContext();

  const sonderState = useSondertagState();
  const dialogs = useSonderDialogs();

  const {
    schoolyears,
    setSchoolyears,
    selectedSchoolyear,
    setSelectedSchoolyear,
    selectedSchoolyearIdRef,

    sonderEvents,
    setSonderEvents,
    selectedSonderEvent,
    setSelectedSonderEvent,
    selectedSonderEventIdRef,

    setSondertage,
    setStatsAngebote,
    loadSonderEventData,
    loadTeacherWorkloads,
    teacherWorkloads,
  } = sonderState;

  //stats
  const { stats: teacherStats, meta } = useMemo(() => {
    if (!Array.isArray(sonderState.statsAngebote)) {
      return {
        stats: {},
        meta: {},
      };
    }

    return computeTeacherStats(teacherWorkloads, sonderState.statsAngebote);
  }, [teacherWorkloads, sonderState.statsAngebote]);

  // ✅ permissions
  const isASondertageEventAdmin = isSondertageEventAdmin(
    currentUser,
    isAnAdmin,
    selectedSonderEvent,
  );

  // ✅ socket
  useSondertagSocket({
    setSchoolyears,
    setSelectedSchoolyear,
    selectedSchoolyearIdRef,

    setSonderEvents,
    setSelectedSonderEvent,
    selectedSonderEventIdRef,

    loadSonderEventData,
    loadTeacherWorkloads,
    setSondertage,
    setStatsAngebote,
  });

  // ✅ loading
  const showLoading = useSonderLoading(sonderEvents);

  /* ---------------- RENDER ---------------- */

  let content;
  if (showLoading) {
    content = <LoadingAppScreen />;
  } else if (schoolyears.length === 0 && sonderEvents.length === 0) {
    content = isASondertageEventAdmin ? (
      <EmptyAdminState onCreateEvent={dialogs.openCreateEvent} />
    ) : (
      <EmptyUserState />
    );
  } else {
    content = <SondertageBody />;
  }

  return (
    <SondertagContext.Provider
      value={{
        ...sonderState,
        isASondertageEventAdmin,
        teacherStats,
        statsMeta: meta,
      }}
    >
      <TemplatePage type="sondertageApp">{content}</TemplatePage>

      {/* ✅ ONLY when admin and empty */}
      {isASondertageEventAdmin && sonderEvents.length === 0 && (
        <AddSonderEventDialog
          open={dialogs.createSonderEventOpen}
          selectedSchoolyear={selectedSchoolyear}
          schoolyears={schoolyears}
          onClose={dialogs.closeCreateSonderEvent}
          onSave={async (data) => {
            await sondertagService.createSonderEvent(data);
            dialogs.closeCreateSonderEvent();
          }}
        />
      )}
    </SondertagContext.Provider>
  );
}

export default memo(SondertageApp);
